import { Card, CardContent } from "@/components/ui/card";
import { providers } from "../../utils";
import { CheckCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { AgentFormDataType } from "../../type";

type ProviderSelectionProps = {
  errors: Record<string, string>;
  selectedProvider: string;
  handleProviderChange: (providerId: string) => void;
  formData: AgentFormDataType;
  setFormData: (formData: (prev: AgentFormDataType) => AgentFormDataType) => void;
  getCostBadgeColor: (cost: string) => string;
};

export default function ProviderSelection({
  errors,
  selectedProvider,
  handleProviderChange,
  formData,
  setFormData,
  getCostBadgeColor,
}: ProviderSelectionProps) {
  const getCurrentProvider = () => providers.find((p) => p.id === selectedProvider);

  return (
    <div>
      <div className="space-y-4">
        <label className="text-sm font-medium">Cloud Provider *</label>
        {errors.provider && <p className="text-xs text-red-500">{errors.provider}</p>}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {providers.map((provider) => {
            const IconComponent = provider.icon;
            const isSelected = selectedProvider === provider.id;

            return (
              <Card
                key={provider.id}
                className={`cursor-pointer transition-all ${
                  isSelected ? "ring-2 ring-blue-500 bg-blue-50" : "hover:shadow-md"
                }`}
                onClick={() => handleProviderChange(provider.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${provider.color}`}>
                      <IconComponent size={20} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-sm">{provider.name}</h3>
                      <p className="text-xs text-gray-600 mt-1">{provider.description}</p>
                      <div className="mt-2">
                        <Badge variant="outline" className="text-xs">
                          {provider.models.length} models
                        </Badge>
                      </div>
                    </div>
                    {isSelected && <CheckCircle className="h-5 w-5 text-blue-500 flex-shrink-0" />}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
      {selectedProvider && (
        <div className="space-y-4">
          <label className="text-sm font-medium">Model *</label>
          {errors.model_name && <p className="text-xs text-red-500">{errors.model_name}</p>}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {getCurrentProvider()?.models.map((model) => (
              <Card
                key={model.id}
                className={`cursor-pointer transition-all ${
                  formData.model_name === model.id
                    ? "ring-2 ring-blue-500 bg-blue-50"
                    : "hover:shadow-md"
                }`}
                onClick={() => setFormData((prev) => ({ ...prev, model_name: model.id }))}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-sm">{model.name}</h4>
                      <p className="text-xs text-gray-600">{model.id}</p>
                    </div>
                    <Badge className={getCostBadgeColor(model.cost)}>{model.cost}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
